import React from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import { Footer, Header } from "./headerFooter";
import Modal from 'react-bootstrap/Modal';
import { useState, useEffect, useCallback } from 'react';
import { pathToFeatureName, trackEvent } from './analytics';
import { WHATS_NEW_UPDATES } from './constants';
import { isAdministratorToken } from './adminUserPremium';
import { useTermsStatus } from './TermsAcceptanceGate';
import './custom.scss';

const MAIN_MENU_ITEMS = [
  { title: 'Εκτιμήσεις πιθανοτήτων πρόσληψης', description: 'Πιθανότητες πρόσληψης ανά περιοχή προτίμησης από ιστορικά δεδομένα.', path: '/predict', isBeta: true },
  { title: 'Θέση τελευταίου διορισθέντα ανά έτος', description: 'Αριθμός διορισμών, θέση τελευταίου διορισθέντα και μόρια ανά έτος ανά κλάδο και περιοχές διορισμού.', path: '/appointments-min-table', highlighted: true },
  { title: 'Αριθμός προσλήψεων ανά ημερομηνία πρόσληψης', description: 'Συνολικός αριθμός προσλήψεων ανά ημερομηνία για έτος, ειδικότητα και περιοχή.', path: '/ranking-per-state' },
  { title: 'Έλεγχος περιοχών βάσει θέσης πίνακα', description: 'Περιοχές που αντιστοιχούν στη θέση σας για συγκεκριμένη χρονιά και φάση.', path: '/ranking-per-score' },
  { title: 'Εύρεση θέσης πίνακα', description: 'Αναζήτηση θέσης με προκήρυξη, επώνυμο και είδος πίνακα (αρχικός/εκκαθαρισμένος).', path: '/ranking-personal-details' },
  { title: 'Ημερομηνία πρόσληψης βάσει σειράς πίνακα', description: 'Εκτίμηση ημερομηνίας πρόσληψης βάσει σχολικής χρονιάς, ειδικότητας και θέσης.', path: '/appointment-date' },
  { title: 'Συνολικός αριθμός εκπαιδευτικών που προηγούνται', description: 'Άτομα που προηγούνται και συνολικές προσλήψεις ανά φάση και περιοχή.', path: '/ranking-above-me' },
  { title: 'Ιστορικά δεδομένα προσλήψεων', description: 'Προσλήψεις προηγούμενων ετών ανά ημερομηνία και περιοχή.', path: '/historical-data' },
  { title: 'Ιστορικά δεδομένα διορισμών', description: 'Δεδομένα διορισμών ανά έτος και περιοχή.', path: '/historical-data-appointments' },
  { title: 'Μοριοδότηση σχολικών μονάδων', description: 'Δυσπρόσιτα σχολεία ανά περιοχή και είδος εκπαίδευσης.', path: '/non-reachable-places' },
  { title: 'Μεταθέσεις εκπαιδευτικών', description: 'Δεδομένα μεταθέσεων τρέχοντος και προηγούμενων ετών.', path: '/transfers' },
  { title: 'Τιμές ενοικίων', description: 'Μέσος όρος τιμών ενοικίων ανά νομό (πηγή: spitogatos.gr).', path: '/rent-prices' },
];

const ADMIN_MENU_ITEM = {
  title: 'Διαχείριση Premium',
  description: 'Έλεγχος και χειροκίνητη ενεργοποίηση user_premium (μόνο administrator).',
  path: '/admin/user-premium',
};

function MainPage() {
    const navigate = useNavigate();
    const [showWelcome, setShowWelcome] = useState(false);
    const isAdmin = isAdministratorToken(localStorage.getItem('authToken'));
    const menuItems = isAdmin ? [...MAIN_MENU_ITEMS, ADMIN_MENU_ITEM] : MAIN_MENU_ITEMS;
    const { needsTerms, checked } = useTermsStatus();

    const tryOpenWelcomeModal = useCallback(() => {
        if (localStorage.getItem('showWelcomeModal') !== 'true') {
            return;
        }

        // TermsAcceptanceGate has already resolved this by the time MainPage
        // mounts (it blocks rendering children until `checked` is true), but
        // guard anyway in case that ever changes.
        if (!checked || needsTerms) {
            return;
        }

        setShowWelcome(true);
    }, [checked, needsTerms]);

    useEffect(() => {
        tryOpenWelcomeModal();
        const onTermsAccepted = () => {
            tryOpenWelcomeModal();
        };
        window.addEventListener('terms-accepted-show-welcome', onTermsAccepted);
        return () => window.removeEventListener('terms-accepted-show-welcome', onTermsAccepted);
    }, [tryOpenWelcomeModal]);
    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.setItem('logout-event', Date.now());
        navigate('/', { replace: true });
    };

    const handleCloseWelcome = () => {
        setShowWelcome(false);
        localStorage.setItem('showWelcomeModal', 'false');
    };

    const handleTileNavigate = (item) => {
        trackEvent('select_content', {
            content_type: 'main_tile',
            feature_name: pathToFeatureName(item.path),
        });
        navigate(item.path);
    };

    return (
        <div id='root'>
            <Header handleLogout={handleLogout} />
            <main className='main-container'>
                <Container className="main-page-container">
                    <Modal
                        show={showWelcome}
                        onHide={handleCloseWelcome}
                        backdrop="static"
                        keyboard={false}
                        centered
                        size="lg"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
                                Καλωσορίσατε στο teachers-app.com!
                                {WHATS_NEW_UPDATES.length > 0 && (
                                    <span className="welcome-modal-new-badge" aria-label="Νέες ενημερώσεις">
                                        <span className="welcome-modal-new-badge-dot" aria-hidden="true" />
                                        ΝΕΕΣ ΠΡΟΣΘΗΚΕΣ
                                    </span>
                                )}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>
                                Η <strong>teachers-app.com</strong> είναι μια <strong>πιλοτική, ενημερωτική</strong> εφαρμογή για{' '}
                                <strong>αναπληρωτές και μόνιμους εκπαιδευτικούς</strong> όλων των ειδικοτήτων. Συγκεντρώνει δημόσια
                                δεδομένα σε εργαλεία αναζήτησης και στατιστικής ανάλυσης.
                            </p>
                            <p className="mb-2">
                                <strong>Μπορείτε να:</strong>
                            </p>
                            <ul>
                                <li>
                                    <strong>Ελέγξετε τη θέση σας</strong> στους πίνακες (εύρεση θέσης, περιοχές ανά κατάταξη,
                                    εκτίμηση ημερομηνίας πρόσληψης)
                                </li>
                                <li>
                                    <strong>Δείτε στατιστικά προσλήψεων και διορισμών</strong> ανά έτος, φάση, ειδικότητα και
                                    περιοχή
                                </li>
                                <li>
                                    <strong>Δοκιμάσετε τις Εκτιμήσεις πιθανοτήτων πρόσληψης (BETA)</strong> — στατιστική εκτίμηση
                                    ανά περιοχή από ιστορικά δεδομένα
                                </li>
                                <li>
                                    <strong>Χρησιμοποιήστε επιπλέον εργαλεία:</strong> μεταθέσεις, μοριοδότηση δυσπρόσιτων
                                    σχολείων, τιμές ενοικίων
                                </li>
                            </ul>
                            {WHATS_NEW_UPDATES.length > 0 && (
                                <div className="welcome-modal-updates mb-3">
                                    <p className="welcome-modal-updates-title mb-2">
                                        <strong>Τελευταία ενημέρωση</strong>
                                    </p>
                                    <ul className="mb-0">
                                        {WHATS_NEW_UPDATES.map((item, i) => (
                                            <li key={i}>
                                                <span className="welcome-modal-updates-date">({item.date})</span>{' '}
                                                <span dangerouslySetInnerHTML={{ __html: item.text }} />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <p>
                                <strong>Πηγές:</strong> επίσημες ανακοινώσεις <strong>Υπ. Παιδείας</strong> (minedu.gov.gr)·
                                ενδεικτικές τιμές ενοικίων από{' '}
                                <a href="https://www.spitogatos.gr/deiktis-timon" target="_blank" rel="noopener noreferrer">
                                    <strong>spitogatos.gr</strong>
                                </a>.
                            </p>
                            <p className="mb-0">
                                <strong>Σημαντικό:</strong> Τα αποτελέσματα είναι για <strong>ενημερωτική χρήση</strong>, όχι
                                επίσημη πρόβλεψη ή εγγύηση τοποθέτησης. Η εφαρμογή <strong>δεν</strong> συνδέεται με το Υπουργείο ή
                                τον ΑΣΕΠ.
                            </p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="primary" onClick={handleCloseWelcome}>Έναρξη</Button>
                        </Modal.Footer>
                    </Modal>

                    <Row className="g-3 main-page-cards">
                    {menuItems.map((item) => (
                            <Col key={item.path} xs={12} sm={6} lg={4} xl={3}>
                                <Card
                                    className={`main-page-card h-100${(item.isBeta || item.highlighted) ? ' main-page-card--beta' : ''}${item.disabled ? ' main-page-card--disabled' : ''}`}
                                    role={item.disabled ? undefined : "button"}
                                    tabIndex={item.disabled ? -1 : 0}
                                    aria-disabled={item.disabled || undefined}
                                    onClick={item.disabled ? undefined : () => handleTileNavigate(item)}
                                    onKeyDown={item.disabled ? undefined : (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleTileNavigate(item);
                                        }
                                    }}
                                >
                                    <Card.Body className="d-flex flex-row align-items-center gap-3">
                                        <div className="d-flex flex-column flex-grow-1 min-w-0">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="main-page-card-title mb-0">{item.title}</span>
                                                {item.isBeta && (
                                                    <span className="main-page-card-beta-badge flex-shrink-0" aria-label="Δοκιμαστική έκδοση">BETA</span>
                                                )}
                                                {item.disabled && (
                                                    <span className="main-page-card-disabled-badge flex-shrink-0">Προσωρινά μη διαθέσιμο</span>
                                                )}
                                            </div>
                                            <Card.Text className="small text-muted mb-0 mt-1">{item.description}</Card.Text>
                                        </div>
                                        {!item.disabled && (
                                            <span className="main-page-card-action flex-shrink-0" aria-hidden>
                                                <i className="bi bi-chevron-right" />
                                            </span>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </main>
            <Footer />
        </div>
    );
}

export default MainPage;